import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import {
	$addPhones,
	$cursorPhones,
	$hasMorePhones,
	$isFetchingPhones,
	$phones,
	$setCursorPhones,
	$setHasMorePhones,
	$setIsFetchingPhones,
	$setPhones,
	type IPhoneListItem,
} from '@/entities/phone.entity';
import {
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$phonesFetchKey,
	$phonesListScrollTop,
	$phonesSearchField,
	$phonesSortBy,
	$setPhonesFetchKey,
	$setPhonesListScrollTop,
	onlineStatusToIsOnline,
	toServerPhonesFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import PhoneListItem from '../cards/phone-list-item';
import { LogoLoader } from '@/components/logo-loader';

const LIMIT = 35;

export default function PhonesList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [phones, addPhones, setPhones] = useUnit([$phones, $addPhones, $setPhones]);
	const [hasMore, setHasMore] = useUnit([$hasMorePhones, $setHasMorePhones]);
	const [cursor, setCursor] = useUnit([$cursorPhones, $setCursorPhones]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingPhones, $setIsFetchingPhones]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$phonesSearchField,
		$phonesSortBy,
		$clientsSortDir,
		$phonesListScrollTop,
		$setPhonesListScrollTop,
		$phonesFetchKey,
		$setPhonesFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerPhonesFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: phones.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 100,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'phones',
		listRef,
		virtualizer,
		items: phones,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && phones.length > 0,
	});

	useEffect(() => {
		if (isDevMode) return;

		function addPhonesHandler(data: { phones: IPhoneListItem[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.phones?.length) {
				addPhones(data.phones);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addPhones, addPhonesHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addPhones, addPhonesHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setPhones([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getPhones, { ...queryPayload, cursor: null });
		}

		if (phones.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [currentFetchKey]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= phones.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getPhones, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, phones.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !phones.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : phones.length === 0 ? (
				<div className='text-center mt-30 text-md'>Номера не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const phone = phones[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<PhoneListItem phone={phone} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
